import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import AuthRight from "./AuthRight";
import { registerUser } from "../../Redux/Actions/authActions";
import { sendVerificationOTPs, verifyOTP } from "../../services/authService";


function Signup({ registerUser }) {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Email/Mobile, 2: OTP Verification, 3: Registration
    const [formData, setFormData] = useState({
        email: "",
        mobileNumber: "",
        emailOTP: "",
        password: "",
        name: "",
        gender: "",
        agentId: "",
        referedBy: "",
    });
    const [verificationToken, setVerificationToken] = useState("");
    const [registrationToken, setRegistrationToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // For mobile number, only allow numbers
        if (name === 'mobileNumber') {
            const numbersOnly = value.replace(/\D/g, '').slice(0, 10);
            setFormData({
                ...formData,
                [name]: numbersOnly
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
        setError("");
        setSuccess("");
    };

    // Step 1: Send OTP
    const handleSendOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        try {
            if (!formData.email || !formData.mobileNumber) {
                setError("Email and Mobile Number are required.");
                setLoading(false);
                return;
            }

            const response = await sendVerificationOTPs({
                email: formData.email.trim(),
                phone: formData.mobileNumber.trim()
            });

            if (response.success || response.data?.verificationToken) {
                setVerificationToken(response.data?.verificationToken || response.verificationToken || "");
                setStep(2);
                setSuccess("OTP sent successfully. Please check your email.");
            } else {
                setError(response.message || "Failed to send OTP. Please try again.");
            }
        } catch (err) {
            setError(err.message || err.data?.message || "Failed to send OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Verify OTP
    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        try {
            if (!formData.emailOTP) {
                setError("Email OTP is required.");
                setLoading(false);
                return;
            }

            const response = await verifyOTP({
                verificationToken: verificationToken,
                emailOTP: formData.emailOTP.trim()
            });

            if (response.success || response.data?.registrationToken) {
                setRegistrationToken(response.data?.registrationToken || response.registrationToken || "");
                setStep(3);
                setSuccess("OTP verified successfully. Please complete your registration.");
            } else {
                setError(response.message || "Invalid OTP. Please try again.");
            }
        } catch (err) {
            setError(err.message || err.data?.message || "OTP verification failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Register User
    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        try {
            // Validate all required fields
            if (!formData.name || !formData.email || !formData.mobileNumber || !formData.password || !formData.agentId || !formData.gender) {
                setError("All fields are required. Please fill in all the information.");
                setLoading(false);
                return;
            }

            const userData = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                mobileNumber: formData.mobileNumber.trim(),
                password: formData.password,
                gender: formData.gender,
                agentId: formData.agentId.trim(),
                referedBy: formData.referedBy.trim() || undefined,
            };

            const result = await registerUser(userData);
            
            if (result.success) {
                setSuccess("Registration successful! Redirecting to login page...");
                // Redirect to login page after 2 seconds
                setTimeout(() => {
                    navigate(process.env.PUBLIC_URL + "/sign-in");
                }, 2000);
            } else {
                setError(result.error || "Registration failed. Please try again.");
            }
        } catch (err) {
            setError("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="body d-flex p-0 p-xl-5">
                <div className="container">
                    <div className="row g-3">
                        <div className="col-lg-6 d-flex justify-content-center align-items-center auth-h100">
                            <div className="d-flex flex-column">
                                <h1>Create Your Account</h1>
                                <span className="text-muted">
                                    {step === 1 && "Enter your email and mobile number to get started"}
                                    {step === 2 && "Enter the OTP sent to your email"}
                                    {step === 3 && "Please fill in all the required information to register"}
                                </span>
                                
                                {/* Step Indicator */}
                                <div className="d-flex justify-content-center mt-3 mb-3">
                                    <div className="d-flex align-items-center">
                                        <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 1 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '30px', height: '30px' }}>1</div>
                                        <div className={`mx-2 ${step >= 2 ? 'text-primary' : 'text-muted'}`} style={{ width: '50px', height: '2px', backgroundColor: step >= 2 ? 'var(--primary-color)' : '#dee2e6' }}></div>
                                        <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 2 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '30px', height: '30px' }}>2</div>
                                        <div className={`mx-2 ${step >= 3 ? 'text-primary' : 'text-muted'}`} style={{ width: '50px', height: '2px', backgroundColor: step >= 3 ? 'var(--primary-color)' : '#dee2e6' }}></div>
                                        <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 3 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '30px', height: '30px' }}>3</div>
                                    </div>
                                </div>

                                <div className="card mt-4">
                                    <div className="card-body p-4">
                                        {success && (
                                            <div className="alert alert-success" role="alert">
                                                {success}
                                            </div>
                                        )}
                                        {error && (
                                            <div className="alert alert-danger" role="alert">
                                                {error}
                                            </div>
                                        )}
                                        
                                        {/* Step 1: Email and Mobile Number */}
                                        {step === 1 && (
                                            <form onSubmit={handleSendOTP}>
                                                <div className="mb-3">
                                                    <label className="form-label fs-6">Email address <span className="text-danger">*</span></label>
                                                    <input 
                                                        type="email" 
                                                        className="form-control"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        required
                                                        disabled={loading}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label fs-6">Mobile Number <span className="text-danger">*</span></label>
                                                    <input 
                                                        type="tel" 
                                                        className="form-control"
                                                        name="mobileNumber"
                                                        value={formData.mobileNumber}
                                                        onChange={handleChange}
                                                        required
                                                        disabled={loading}
                                                        maxLength="10"
                                                        pattern="[0-9]*"
                                                        inputMode="numeric"
                                                        placeholder="Enter 10 digit mobile number"
                                                    />
                                                </div>
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary text-uppercase py-2 fs-5 w-100 mt-2"
                                                    disabled={loading}
                                                >
                                                    {loading ? "Sending OTP..." : "Send OTP"}
                                                </button>
                                            </form>
                                        )}

                                        {/* Step 2: OTP Verification */}
                                        {step === 2 && (
                                            <form onSubmit={handleVerifyOTP}>
                                                <div className="mb-3">
                                                    <label className="form-label fs-6">Email OTP <span className="text-danger">*</span></label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control text-center"
                                                        name="emailOTP"
                                                        value={formData.emailOTP}
                                                        onChange={handleChange}
                                                        required
                                                        disabled={loading}
                                                        maxLength="6"
                                                        placeholder="Enter 6 digit OTP"
                                                        style={{ fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                                                    />
                                                    <small className="text-muted">OTP sent to {formData.email}</small>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-outline-secondary text-uppercase py-2 fs-5 flex-fill"
                                                        onClick={() => setStep(1)}
                                                        disabled={loading}
                                                    >
                                                        Back
                                                    </button>
                                                    <button 
                                                        type="submit" 
                                                        className="btn btn-primary text-uppercase py-2 fs-5 flex-fill"
                                                        disabled={loading}
                                                    >
                                                        {loading ? "Verifying..." : "Verify OTP"}
                                                    </button>
                                                </div>
                                            </form>
                                        )}

                                        {/* Step 3: Registration Form */}
                                        {step === 3 && (
                                            <form onSubmit={handleRegister}>
                                                        <div className="mb-3">
                                                <label className="form-label fs-6">Full Name <span className="text-danger">*</span></label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control"
                                                                name="name"
                                                                value={formData.name}
                                                                onChange={handleChange}
                                                                required
                                                                disabled={loading}
                                                            />
                                                        </div>
                                                        <div className="mb-3">
                                                <label className="form-label fs-6">Email address <span className="text-danger">*</span></label>
                                                            <input 
                                                                type="email" 
                                                                className="form-control"
                                                                name="email"
                                                                value={formData.email}
                                                                readOnly
                                                                disabled
                                                                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                                            />
                                                        </div>
                                                        <div className="mb-3">
                                                <label className="form-label fs-6">Mobile Number <span className="text-danger">*</span></label>
                                                            <input 
                                                    type="text" 
                                                                className="form-control"
                                                    name="mobileNumber"
                                                    value={formData.mobileNumber}
                                                                readOnly
                                                                disabled
                                                                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                                            />
                                                        </div>
                                                        <div className="mb-3">
                                                <label className="form-label fs-6">Gender <span className="text-danger">*</span></label>
                                                <select 
                                                    className="form-select"
                                                    name="gender"
                                                    value={formData.gender}
                                                                onChange={handleChange}
                                                    required
                                                            disabled={loading}
                                                        >
                                                    <option value="">Select Gender</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                    <option value="other">Other</option>
                                                </select>
                                                        </div>
                                                        <div className="mb-3">
                                                <label className="form-label fs-6">Agent ID <span className="text-danger">*</span></label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control"
                                                    name="agentId"
                                                    value={formData.agentId}
                                                                onChange={handleChange}
                                                                required
                                                                disabled={loading}
                                                            />
                                                        </div>
                                                        <div className="mb-3">
                                                <label className="form-label fs-6">Password <span className="text-danger">*</span></label>
                                                            <input 
                                                                type="password" 
                                                                className="form-control"
                                                                name="password"
                                                                value={formData.password}
                                                                onChange={handleChange}
                                                                required
                                                                disabled={loading}
                                                            />
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="form-label fs-6">Referral Code (Optional)</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control"
                                                    name="referedBy"
                                                    value={formData.referedBy}
                                                                onChange={handleChange}
                                                                disabled={loading}
                                                            />
                                                        </div>
                                                <div className="d-flex gap-2 mt-3">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-outline-secondary text-uppercase py-2 fs-5 flex-fill"
                                                        onClick={() => setStep(2)}
                                                        disabled={loading}
                                                    >
                                                        Back
                                                    </button>
                                                    <button 
                                                        type="submit" 
                                                        className="btn btn-primary text-uppercase py-2 fs-5 flex-fill"
                                                        disabled={loading}
                                                    >
                                                        {loading ? "Creating Account..." : "Create Account"}
                                                    </button>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </div>
                                <Link to={process.env.PUBLIC_URL+"/sign-in"} title="#" className="mt-3">Already registered? <span className="text-secondary text-decoration-underline">Log In</span></Link>
                            </div>
                        </div>
                        <AuthRight />
                    </div>
                </div>
            </div>   
        </>
    )
}

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps, { registerUser })(Signup);